import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  InputNumber,
  List,
  Modal,
  notification,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useMutation, useQueryClient } from 'react-query';

import { handleAPIError } from '@services/base';
import useModal, { IModalPayload } from '@hooks/use-modal';

import { IInvoice, IInvoicePenalty } from '@services/invoice/invoice.model';
import { getInvoices, undoBillingPerPenalty } from '@services/invoice';
import { getPenalty, IPenaltyBase, undoBilling } from '@services/penalty';
import { CircleCheck, FilePlus } from 'lucide-react';

type PenaltyWithMeta = IInvoicePenalty & {
  percentage: number;
  invoiceNumber?: string;
  sennder_reference?: string;
  static_data?: {
    sennder_reference?: string;
    order_date?: string;
  };
};

const CreateInvoiceRdaModal = () => {
  const [modal, setModal] = useModal<IModalPayload<any>>('invoice-rda');
  const [data, setData] = useState<IInvoice[]>([]);
  const [penalties, setPenalties] = useState<PenaltyWithMeta[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const { Title, Text } = Typography;
  const onClose = () => {
    setModal({ isOpen: false, payload: null });
    setData([]);
    setPenalties([]);
  };
  console.log('modal payload', modal?.payload);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!modal?.isOpen) {
      return;
    }

    const fetchPenalties = async () => {
      if (!modal?.payload?.ids?.length) {
        setData([]);
        setPenalties([]);
        return;
      }

      if (modal?.payload?.type === 'penalty') {
        setIsFetching(true);
        try {
          const penaltyPromises = (modal.payload.ids as string[]).map(
            (id: string) => getPenalty(id),
          );
          const responses = await Promise.all(penaltyPromises);

          const penaltiesWithPercentage = responses.map(response => {
            const resp = response as any;
            const penaltyData: IPenaltyBase = resp.penalty ?? resp;

            const amount =
              penaltyData.billed_amount ||
              (penaltyData.penalty_percentage && penaltyData.total_net_cost
                ? Number(penaltyData.penalty_percentage) *
                  Number(penaltyData.total_net_cost)
                : 0);

            return {
              _id: penaltyData._id,
              amount: Number(amount),
              percentage: 100,
              sennder_reference: penaltyData.sennder_reference,
              data: {
                _id: penaltyData._id,
                sennder_reference: penaltyData.sennder_reference || '',
                order_date: penaltyData.order_date || '',
              },
              competence: penaltyData.competence || '',
            } as PenaltyWithMeta;
          });

          setPenalties(penaltiesWithPercentage);
          setData([]);
        } catch (error) {
          handleAPIError(error);
          setPenalties([]);
        } finally {
          setIsFetching(false);
        }
        return;
      }

      setIsFetching(true);
      try {
        const response = await getInvoices({
          invoice_number: modal.payload.ids,
        });
        const invoices =
          (response as { data?: IInvoice[] })?.data ??
          (response as { invoices?: IInvoice[] })?.invoices ??
          [];
        setData(invoices);
        const penaltiesWithPercentage = invoices.flatMap(invoice =>
          (invoice.penalties || []).map(penalty => ({
            ...penalty,
            _id:
              typeof penalty.data === 'string'
                ? penalty.data
                : penalty.data?._id || '',
            invoiceNumber: invoice.invoice_number,
            percentage: 100,
          })),
        );
        setPenalties(penaltiesWithPercentage);
      } catch (error) {
        handleAPIError(error);
        setPenalties([]);
      } finally {
        setIsFetching(false);
      }
    };

    fetchPenalties();
  }, [modal?.isOpen, modal?.payload?.ids, modal?.payload?.type]);

  const handlePercentageChange = (penaltyId: string, value: number | null) => {
    setPenalties(prevPenalties =>
      prevPenalties.map(penalty =>
        penalty._id === penaltyId
          ? {
              ...penalty,
              percentage: value ?? 100,
            }
          : penalty,
      ),
    );
  };

  const penaltyPercentageMap = useMemo(() => {
    return penalties.reduce<Record<string, number>>((acc, penalty) => {
      acc[penalty._id] = penalty.percentage;
      return acc;
    }, {});
  }, [penalties]);

  const hasZeroAmountInvoice = () => {
    if (!modal?.payload) return false;

    if (modal.payload.type === 'penalty') {
      return penalties.some(penalty => penalty.percentage === 0);
    }
    return penalties.some(
      penalty =>
        penalty.amount &&
        penalty.amount > 0 &&
        (penaltyPercentageMap[penalty._id] ?? 100) === 0,
    );
  };

  const { isLoading: isSubmitting, mutate } = useMutation(
    () => {
      const penaltiesPayload = penalties.map(({ _id, percentage }) => ({
        _id,
        percentage: percentage ?? 100,
      }));
      return modal.payload.type === 'invoice'
        ? undoBilling(modal?.payload.ids, penaltiesPayload)
        : undoBillingPerPenalty(modal?.payload.ids, penaltiesPayload);
    },
    {
      onSuccess: async res => {
        onClose();
        await queryClient.invalidateQueries('invoices');
        res.forEach((result: any) => {
          if (result.success) {
            notification.success({
              message: `(${result.id}) RDA created successfully`,
            });
          } else
            notification.error({
              description: result.error,
              message: `(${result.id}) Cannot create RDA`,
            });
        });
      },
      onError: handleAPIError,
    },
  );

  const submit = () => {
    mutate();
  };

  return (
    <Modal
      destroyOnClose
      centered
      width={720}
      onCancel={onClose}
      footer={null}
      title={
        <>
          <FilePlus size={14} className={'mr-2'} />
          Create RDA
          {data?.length ? <span className="ml-1">({data.length})</span> : null}
        </>
      }
      visible={modal?.isOpen || false}
    >
      <Spin spinning={isSubmitting || isFetching}>
        <div
          style={{
            maxHeight: '55vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            paddingRight: 8,
          }}
        >
          <b>Are you sure you want to create an RDA?</b>
          <div className="mt-4">
            <Title level={5} style={{ marginBottom: 12 }}>
              Penalties overview
            </Title>
            {penalties.length === 0 ? (
              <Empty description="No penalties found for the selected invoices" />
            ) : (
              <Card size="small" bordered={false} style={{ padding: 0 }}>
                <List
                  dataSource={penalties}
                  split
                  renderItem={penalty => {
                    const percentage = penaltyPercentageMap[penalty._id] ?? 100;
                    return (
                      <List.Item
                        key={penalty._id}
                        actions={[
                          <Space key="controls" size={12} align="center">
                            <Text strong>
                              {(
                                (penalty.amount ?? 0) *
                                (percentage / 100)
                              ).toLocaleString('it-IT', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              €
                            </Text>
                            <InputNumber
                              min={0}
                              max={100}
                              formatter={value => `${value ?? 0}%`}
                              parser={value => {
                                const numericValue = value
                                  ? Number(value.replace(/%/g, ''))
                                  : 0;
                                return Number.isNaN(numericValue)
                                  ? 0
                                  : numericValue;
                              }}
                              value={percentage}
                              onChange={value =>
                                handlePercentageChange(penalty._id, value)
                              }
                              style={{ width: 100 }}
                            />
                          </Space>,
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <Text strong>
                              {penalty.static_data?.sennder_reference ||
                                penalty.data?.sennder_reference ||
                                penalty.sennder_reference ||
                                penalty._id}
                            </Text>
                          }
                          description={
                            <Space direction="horizontal" size={12}>
                              {penalty.invoiceNumber ? (
                                <Text type="secondary">
                                  Invoice #{penalty.invoiceNumber}
                                </Text>
                              ) : null}
                              {penalty.static_data?.order_date ||
                              penalty.data?.order_date ? (
                                <Text type="secondary">
                                  {new Date(
                                    penalty.static_data?.order_date ||
                                      penalty.data.order_date,
                                  ).toLocaleDateString('it-IT', {
                                    timeZone: 'Europe/Rome',
                                  })}
                                </Text>
                              ) : null}
                            </Space>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </Card>
            )}
          </div>
        </div>
        <br />
        <div className={'text-center'}>
          <Space align={'center'}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={submit}
              type="primary"
              icon={<CircleCheck size={14} className="mr-2" />}
              disabled={hasZeroAmountInvoice()}
            >
              Confirm
            </Button>
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};

export default CreateInvoiceRdaModal;
